"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Architecture() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const pillar1 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillar2 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillar3 = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "Diagnostic Precision",
    description: "Lumina maps student responses directly to mark-scheme logic. We identify the exact point of failure in a process, distinguishing between a conceptual gap and a simple procedural slip.",
    ref: pillar1.ref,
    isVisible: pillar1.isVisible,
    delay: 0,
    gridClasses: "lg:col-start-1 lg:col-span-5 lg:row-start-1",
    offsetClass: "lg:mt-0"
  }, {
    number: "02",
    title: "Process Integrity",
    description: "Most marks are lost during execution, not through lack of knowledge. Our engine tracks every step of the student's method, ensuring that the logic remains sound from start to finish.",
    ref: pillar2.ref,
    isVisible: pillar2.isVisible,
    delay: 200,
    gridClasses: "lg:col-start-6 lg:col-span-5 lg:row-start-1",
    offsetClass: "lg:mt-32"
  }, {
    number: "03",
    title: "Actionable Insight",
    description: "We transform raw assessment data into clear, pedagogical feedback. Teachers receive immediate clarity on where to intervene, allowing for targeted support that actually moves the needle.",
    ref: pillar3.ref,
    isVisible: pillar3.isVisible,
    delay: 400,
    gridClasses: "lg:col-start-3 lg:col-span-6 lg:row-start-2",
    offsetClass: "lg:mt-16"
  }];
  return <section data-section-id="3189" id="scalable-architecture" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      {}
      <div className="hidden lg:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-center gap-3 font-mono text-xs tracking-[0.25em] text-muted-foreground">
        <span className={pillar1.isVisible ? "opacity-100" : "opacity-40"}>01</span>
        <span className="opacity-60">→</span>
        <span className={pillar2.isVisible ? "opacity-100" : "opacity-40"}>02</span>
        <span className="opacity-60">→</span>
        <span className={pillar3.isVisible ? "opacity-100" : "opacity-40"}>03</span>
      </div>

      <div className="max-w-7xl mx-auto">
        {}
        <div ref={eyebrow.ref} className={`font-mono text-xs md:text-sm mb-12 transition-all duration-700 ease-out text-primary ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        letterSpacing: "0.25em"
      }}>
          — 02 / THE ARCHITECTURE
        </div>

        {}
        <h2 ref={heading.ref} className={`font-default text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1] max-w-3xl mb-24 md:mb-32 transition-all duration-1000 ease-out text-foreground ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Precision engineering for{" "}
          <em className="italic font-light text-primary">
            educational clarity
          </em>
          .
        </h2>

        {}
        <div className="relative lg:grid lg:grid-cols-10 lg:gap-8">
          {}
          <svg className="hidden lg:block absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 800" preserveAspectRatio="none" aria-hidden="true">
            <path d="M 200 120 Q 400 200, 600 240" fill="none" stroke="currentColor" className="text-border" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="2 4" />
            <path d="M 700 320 Q 600 480, 400 560" fill="none" stroke="currentColor" className="text-border" strokeOpacity="0.4" strokeWidth="1" strokeDasharray="2 4" />
          </svg>

          {pillars.map((pillar, i) => <div key={pillar.number} data-index={i} ref={pillar.ref} className={`${pillar.gridClasses} ${pillar.offsetClass} mb-16 lg:mb-0 ${i === 1 ? "lg:pl-8" : ""} ${i === 2 ? "lg:pl-12" : ""} transition-all duration-1000 ease-out ${pillar.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          transitionDelay: `${pillar.delay}ms`
        }}>
              {}
              <div className="font-mono text-7xl md:text-8xl font-light mb-6 leading-none text-primary">
                {pillar.number}
              </div>

              {}
              <h3 className="font-default font-medium text-3xl md:text-4xl lg:text-[2.5rem] leading-tight mb-6 text-foreground">
                {pillar.title}
              </h3>

              {}
              <p className="font-default text-base md:text-lg leading-relaxed mb-8 max-w-md text-muted-foreground">
                {pillar.description}
              </p>

              {}
              <div className="h-px w-24 bg-border" />
            </div>)}
        </div>
      </div>
    </section>;
}